import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';
import { filterEmptyFields } from 'utils/object';
import {
  cacheByIdArg,
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  Customer,
  CustomersListData,
  CustomerEditRequest,
  CustomersListRequest,
  CustomerCreateRequest,
  CustomersCheckoutListRequest,
  CustomersCheckoutListResponse,
} from './types';

const tagType = 'Customers';

export const customersApi = createApi({
  baseQuery: apiQuery,
  tagTypes: [tagType],
  reducerPath: 'customersApi',
  endpoints: (build) => ({
    get: build.query<Customer, string>({
      providesTags: cacheByIdArg(tagType),
      query: (id) => ({
        method: 'get',
        url: URL.CUSTOMER_DETAILS.replace(':id', id),
      }),
    }),
    remove: build.mutation<void, string>({
      invalidatesTags: invalidatesList(tagType),
      query: (id) => ({
        method: 'delete',
        url: URL.CUSTOMER_DETAILS.replace(':id', id),
      }),
    }),
    create: build.mutation<Customer, CustomerCreateRequest>({
      invalidatesTags: invalidatesList(tagType),
      query: (data) => ({
        method: 'post',
        url: URL.CUSTOMERS_LIST,
        data: filterEmptyFields({
          ...data,
          shipments: data?.shipments?.filter(
            (shipment) => Object.values(shipment).length
          ).length
            ? JSON.stringify(data.shipments)
            : undefined,
        }),
      }),
    }),
    edit: build.mutation<Customer, CustomerEditRequest>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      query: ({ id, ...data }) => ({
        method: 'patch',
        url: URL.CUSTOMER_DETAILS.replace(':id', id),
        data: filterEmptyFields({
          ...data,
          shipments: data?.shipments?.filter(
            (shipment) => Object.values(shipment).length
          )?.length
            ? JSON.stringify(data.shipments)
            : undefined,
        }),
      }),
    }),
    list: build.query<CustomersListData, CustomersListRequest | void>({
      providesTags: (result) => providesList(tagType)(result?.rows),
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };
        return {
          method: 'get',
          url: addParamsToUrl(URL.CUSTOMERS_LIST, searchParams),
        };
      },
    }),
    checkouts: build.query<
      CustomersCheckoutListResponse,
      CustomersCheckoutListRequest | void
    >({
      providesTags: (result) => providesList(tagType)(result?.rows),
      query: (params) => {
        const { id, page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };
        return {
          method: 'get',
          url: addParamsToUrl(
            generatePath(URL.CUSTOMER_CHECKOUTS, { id: String(id) }),
            searchParams
          ),
        };
      },
    }),
  }),
});

export const {
  useGetQuery,
  useListQuery,
  useEditMutation,
  useCreateMutation,
  useCheckoutsQuery,
  useRemoveMutation,
} = customersApi;

export type { Customer, CustomerEditRequest, CustomerCreateRequest };
